import React, { useEffect, useRef, useState } from "react";
import { File } from "../../../../models/File";
import { useFile } from "../../../../contexts/FileContext";
import { useSocketEventByIds } from "../../../../hooks/useSocketEventByIds";
import { create } from "domain";
import EvidenceUploadItem from "./EvidenceUploadItem";

interface EvidenceUploadPanelProps {
  intakeId: string | undefined;
  files: File[];
}

const EvidenceUploadPanel: React.FC<EvidenceUploadPanelProps> = ({
  intakeId,
  files,
}) => {
  const { createFile } = useFile();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(files);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      Array.isArray(files) &&
      files.length > 0 &&
      typeof files[0] === "object" &&
      files[0] !== null
    ) {
      setUploadedFiles(files);
    }
  }, [files]);

  useEffect(() => {
    if (intakeId) {
      setUploadedFiles((prev) =>
        prev.filter((file) => file.intakeId === intakeId),
      );
    }
  }, [intakeId]);

  useSocketEventByIds("files:changed", ["*"], (updatedFile: File) => {
    if (updatedFile.intakeId === intakeId) {
      setUploadedFiles((prev) => {
        const index = prev.findIndex((file) => file._id === updatedFile._id);

        if (index !== -1) {
          console.log("Switching to updated file");
          const updatedFiles = [...prev];
          updatedFiles[index] = { ...prev[index], ...updatedFile };
          return updatedFiles;
        } else {
          console.log("Adding new file");
          return [...prev, updatedFile];
        }
      });
    }
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !intakeId) return;

    try {
      await Promise.all(
        Array.from(files).map(async (file) =>
          createFile(file, {
            name: file.name,
            type: "evidence",
            size: file.size,
            intakeId,
            status: "uploading",
            mimeType: file.type,
          }),
        ),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (fileInputRef.current) {
      fileInputRef.current.files = e.dataTransfer.files;
      handleFileChange({ target: { files: e.dataTransfer.files } } as any);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <h2 className="mb-4 text-lg font-semibold">Upload Files</h2>

      {/* Upload Box */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className="flex flex-col items-center justify-center p-6 text-center border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
        <p className="text-gray-800 dark:text-white">
          Drag and drop files here
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          or click to upload
        </p>
      </div>

      {/* Uploaded Files */}
      <div className="flex flex-col mt-6 space-y-2 overflow-y-auto">
        {uploadedFiles.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No files uploaded yet.
          </p>
        )}
        {uploadedFiles.map((file) => (
          <EvidenceUploadItem key={file._id} file={file} />
        ))}
      </div>
    </div>
  );
};

export default EvidenceUploadPanel;
